import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    primary: {
      main: '#0A1929',
      light: '#1A3A5C',
      dark: '#060F1A',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#1976D2',
      light: '#42A5F5',
      dark: '#0D47A1',
    },
    success: {
      main: '#1B8A2D',
      light: '#E8F5E9',
      dark: '#14662A',
    },
    error: {
      main: '#C62828',
      light: '#FFEBEE',
      dark: '#8E0000',
    },
    warning: {
      main: '#E65100',
      light: '#FFF3E0',
    },
    background: {
      default: '#F4F6F9',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#1A2027',
      secondary: '#5A6A7A',
    },
    divider: '#E3E8EF',
  },
  typography: {
    fontFamily: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    h4: { fontWeight: 700, letterSpacing: '-0.02em', fontSize: '1.75rem' },
    h5: { fontWeight: 700, letterSpacing: '-0.01em', fontSize: '1.35rem' },
    h6: { fontWeight: 600, letterSpacing: '-0.01em', fontSize: '1.1rem' },
    subtitle1: { fontWeight: 500, fontSize: '0.95rem', color: '#5A6A7A' },
    subtitle2: { fontWeight: 600, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#5A6A7A' },
    body1: { fontSize: '0.9rem', lineHeight: 1.6 },
    body2: { fontSize: '0.825rem', lineHeight: 1.5 },
    caption: { fontSize: '0.75rem', letterSpacing: '0.01em' },
    button: { fontWeight: 600, letterSpacing: '0.02em' },
  },
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0 1px 2px rgba(10,25,41,0.06)',
    '0 1px 4px rgba(10,25,41,0.08)',
    '0 2px 8px rgba(10,25,41,0.08)',
    '0 4px 12px rgba(10,25,41,0.08)',
    '0 4px 16px rgba(10,25,41,0.10)',
    '0 6px 20px rgba(10,25,41,0.10)',
    '0 8px 24px rgba(10,25,41,0.12)',
    '0 8px 28px rgba(10,25,41,0.12)',
    '0 10px 32px rgba(10,25,41,0.14)',
    ...Array(15).fill('0 10px 32px rgba(10,25,41,0.14)'),
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#F4F6F9',
        },
        '&::-webkit-scrollbar': {
          width: 6,
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#C4CDD5',
          borderRadius: 3,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 6,
          fontSize: '0.825rem',
          padding: '8px 20px',
        },
        containedPrimary: {
          backgroundColor: '#0A1929',
          '&:hover': { backgroundColor: '#1A3A5C' },
        },
        containedSecondary: {
          backgroundColor: '#1976D2',
          '&:hover': { backgroundColor: '#1565C0' },
        },
        outlined: {
          borderWidth: 1.5,
          '&:hover': { borderWidth: 1.5 },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 4px rgba(10,25,41,0.06), 0 1px 2px rgba(10,25,41,0.04)',
          border: '1px solid #E3E8EF',
          borderRadius: 10,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            fontWeight: 600,
            fontSize: '0.62rem',
            textTransform: 'none',
            letterSpacing: '0.02em',
            backgroundColor: '#F4F6F9',
            color: '#5A6A7A',
            borderBottom: '2px solid #E3E8EF',
            padding: '6px 8px',
            whiteSpace: 'nowrap',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '0.7rem',
          padding: '4px 8px',
          borderBottom: '1px solid #F0F3F7',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: '#F8FAFC !important',
          },
          '&.Mui-selected': {
            backgroundColor: '#EBF5FF !important',
            '&:hover': {
              backgroundColor: '#DCEEFB !important',
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          fontSize: '0.725rem',
          height: 26,
        },
        sizeSmall: {
          height: 22,
          fontSize: '0.7rem',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            fontSize: '0.875rem',
            '& fieldset': {
              borderColor: '#D0D7DE',
            },
            '&:hover fieldset': {
              borderColor: '#1976D2',
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          fontSize: '0.825rem',
          borderRadius: 8,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
        },
      },
    },
  },
});

export default theme;
